import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import BookingRequest, {
  CalendarSlot,
} from "src/app/models/screens/requestManagement.model";
import DashboardScreenViewModel, {
  DashboardLogEntry,
  DetachRequestItem,
} from "src/app/screens/dashbaord/dashboard.viewmodel";
import styles from "src/app/styles/screens/dashboad.style";

const DashboardScreenView: React.FC = () => {
  const {
    role,
    selectorUsers,
    isLoadingChildren,
    selectedDashboardOwnerId,
    onSelectedDashboardOwnerChanged,

    receivedRequests,
    isLoadingReceivedRequests,
    onAcceptRequest,
    onRejectRequest,

    todayEvents,
    isLoadingTodayEvents,
    highlightedEventId,

    logs,
    isLoadingLogs,

    detachRequests,
    isLoadingDetachRequests,
    processingDetachRequestId,
    onDetachPressed,
    onDismissPressed,

    minutesToLabel,
    getStatusChipColor,

    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    isFirstTimeVisiting,
    setIsFirstTimeVisiting,
  } = DashboardScreenViewModel();

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Dashboard" />

      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Dashboard
            </Typography>
          </Grid>

          {role === "Parent" && (
            <Grid container justifyContent="flex-end" mt={0} size={12}>
              <Grid size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}>
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="dashboard-owner-select-label">
                      Dashboard Owner
                    </InputLabel>
                    <Select
                      labelId="dashboard-owner-select-label"
                      value={selectedDashboardOwnerId}
                      label="Dashboard Owner"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedDashboardOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {selectorUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={3}>
                  {role !== "Guest" && (
                    <Grid size={{ xs: 12 }}>
                      <Paper elevation={0} sx={styles.sectionCard}>
                        <Box sx={styles.sectionHeader}>
                          <DashboardIcon sx={styles.sectionIcon} />
                          <Typography sx={styles.sectionTitle}>
                            Received Requests
                          </Typography>
                        </Box>

                        {isLoadingReceivedRequests ? (
                          <Box sx={styles.loaderContainer}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : receivedRequests.length === 0 ? (
                          <Typography sx={styles.emptyStateText}>
                            No requests received.
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {receivedRequests.map((request: BookingRequest) => (
                              <Grid key={request.id} size={{ xs: 12 }}>
                                <Paper
                                  elevation={0}
                                  sx={[
                                    styles.innerCard,
                                    request.emergency &&
                                    request.status === "Pending"
                                      ? styles.emergencyInnerCard
                                      : {},
                                  ]}
                                >
                                  <Box sx={styles.requesterNameContainer}>
                                    <Typography sx={styles.requesterNameText}>
                                      {request.requesterFullName}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={request.status}
                                      color={getStatusChipColor(request.status)}
                                    />
                                  </Box>

                                  <Typography sx={styles.requestMetaText}>
                                    Date: {request.date}
                                  </Typography>

                                  <Typography sx={styles.requestMetaText}>
                                    {request.emergency
                                      ? `Time: ${minutesToLabel(request.from)}`
                                      : `Time: ${minutesToLabel(
                                          request.from,
                                        )} - ${minutesToLabel(request.to)}`}
                                  </Typography>

                                  <Typography
                                    sx={styles.requestDescriptionText}
                                  >
                                    Description: {request.description}
                                  </Typography>

                                  {request.status === "Pending" && (
                                    <Box sx={styles.requestActionsContainer}>
                                      <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleOutlineIcon />}
                                        onClick={() =>
                                          onAcceptRequest(request.id)
                                        }
                                      >
                                        Accept
                                      </Button>

                                      <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CloseIcon />}
                                        onClick={() =>
                                          onRejectRequest(request.id)
                                        }
                                      >
                                        Reject
                                      </Button>
                                    </Box>
                                  )}
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Paper>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={styles.sectionCard}>
                      <Box sx={styles.sectionHeader}>
                        <DescriptionIcon sx={styles.sectionIcon} />
                        <Typography sx={styles.sectionTitle}>
                          Recent Logs
                        </Typography>
                      </Box>

                      {isLoadingLogs ? (
                        <Box sx={styles.loaderContainer}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : logs.length === 0 ? (
                        <Typography sx={styles.emptyStateText}>
                          No logs found.
                        </Typography>
                      ) : (
                        <Box sx={styles.logsListContainer}>
                          {logs.map((log: DashboardLogEntry) => (
                            <Paper
                              key={log.id}
                              elevation={0}
                              sx={styles.innerCard}
                            >
                              <Box sx={styles.logHeaderRow}>
                                <Typography sx={styles.logIdText}>
                                  {log.id}
                                </Typography>
                              </Box>

                              <Typography sx={styles.logText}>
                                {log.text}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={styles.sectionCard}>
                      <Box sx={styles.sectionHeader}>
                        <DashboardIcon sx={styles.sectionIcon} />
                        <Typography sx={styles.sectionTitle}>
                          Today's Schedule
                        </Typography>
                      </Box>

                      {isLoadingTodayEvents ? (
                        <Box sx={styles.loaderContainer}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : todayEvents.length === 0 ? (
                        <Typography sx={styles.emptyStateText}>
                          Nothing scheduled for today.
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          {todayEvents.map((event: CalendarSlot) => (
                            <Grid key={event.id} size={{ xs: 12 }}>
                              <Paper
                                elevation={0}
                                sx={[
                                  styles.innerCard,
                                  highlightedEventId === event.id
                                    ? styles.highlightedInnerCard
                                    : {},
                                ]}
                              >
                                {event.requesterFullName && (
                                  <Typography sx={styles.requesterNameText}>
                                    Appointment with {event.requesterFullName}
                                  </Typography>
                                )}

                                <Typography sx={styles.requestMetaText}>
                                  {event.emergency
                                    ? `Time: ${minutesToLabel(event.from)}`
                                    : `Time: ${minutesToLabel(
                                        event.from,
                                      )} - ${minutesToLabel(event.to)}`}
                                </Typography>

                                <Typography sx={styles.requestDescriptionText}>
                                  Description: {event.description}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Paper>
                  </Grid>

                  {role === "Admin" && (
                    <Grid size={{ xs: 12 }}>
                      <Paper elevation={0} sx={styles.sectionCard}>
                        <Box sx={styles.sectionHeader}>
                          <DashboardIcon sx={styles.sectionIcon} />
                          <Typography sx={styles.sectionTitle}>
                            Detach Requests
                          </Typography>
                        </Box>

                        {isLoadingDetachRequests ? (
                          <Box sx={styles.loaderContainer}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : detachRequests.length === 0 ? (
                          <Typography sx={styles.emptyStateText}>
                            No detach requests available.
                          </Typography>
                        ) : (
                          <Box sx={styles.logsListContainer}>
                            {detachRequests.map(
                              (request: DetachRequestItem) => (
                                <Paper
                                  key={request.model.id}
                                  elevation={0}
                                  sx={styles.innerCard}
                                >
                                  <Typography sx={styles.requesterNameText}>
                                    {request.model.fullName}
                                  </Typography>

                                  <Typography sx={styles.requestMetaText}>
                                    Birthdate: {request.model.birthdate}
                                  </Typography>

                                  <Typography sx={styles.requestMetaText}>
                                    Parents:{" "}
                                    {request.parents.length > 0
                                      ? request.parents
                                          .map((parent) => parent.fullName)
                                          .join(", ")
                                      : "No linked parents"}
                                  </Typography>

                                  <Box sx={styles.requestActionsContainer}>
                                    <Button
                                      variant="contained"
                                      color="warning"
                                      loading={
                                        processingDetachRequestId ===
                                        request.model.id
                                      }
                                      disabled={
                                        processingDetachRequestId ===
                                        request.model.id
                                      }
                                      onClick={() => onDetachPressed(request)}
                                    >
                                      Accept
                                    </Button>

                                    <Button
                                      variant="outlined"
                                      color="inherit"
                                      loading={
                                        processingDetachRequestId ===
                                        request.model.id
                                      }
                                      disabled={
                                        processingDetachRequestId ===
                                        request.model.id
                                      }
                                      onClick={() => onDismissPressed(request)}
                                    >
                                      Reject
                                    </Button>
                                  </Box>
                                </Paper>
                              ),
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>
      <Dialog
        open={isFirstTimeVisiting}
        onClose={() => setIsFirstTimeVisiting(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Welcome!</DialogTitle>
        <DialogContent>
          <Typography>Welcome to W.A.R.P.A</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsFirstTimeVisiting(false)}
            variant="contained"
            color="primary"
          >
            Get Started
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default DashboardScreenView;
